import { useState, type JSX } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';
import { getApiErrorUserMessage } from '../api/error-utils';
import { useTenancyStore } from './tenancy-store';

type ChangeEventLike = { target: { value?: unknown } };

export function CompanySelectionScreen(): JSX.Element {
  const auth = useAuth();
  const bootstrap = auth.bootstrap;
  const isAuthLoading = auth.isLoading;
  const location = useLocation();
  const navigate = useNavigate();
  const selectedCompanyId = useTenancyStore((state) => state.selectedCompanyId);
  const setSelectedCompanyId = useTenancyStore((state) => state.setSelectedCompanyId);
  const memberships = bootstrap?.memberships ?? [];
  const hasPlatformOption = Boolean(auth.canAccessPlatform);
  const selectedMembership =
    memberships.find((membership) => membership.companyId === selectedCompanyId) ?? null;
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  const handleCompanyChange = async (event: ChangeEventLike): Promise<void> => {
    const rawValue = event.target.value;
    const nextCompanyId = typeof rawValue === 'string' ? rawValue.trim() : '';
    setSelectedCompanyId(nextCompanyId || null);
    setSelectionError(null);
    if (!nextCompanyId) {
      return;
    }

    setIsSelecting(true);
    try {
      if (!auth.selectCompany) {
        throw new Error('Company selection is unavailable in this context');
      }
      await auth.selectCompany(nextCompanyId);
      navigate('/', { replace: true });
    } catch (error) {
      setSelectionError(getApiErrorUserMessage(error));
    } finally {
      setIsSelecting(false);
    }
  };

  const handleOpenSelectedCompany = async (): Promise<void> => {
    if (!selectedMembership) {
      return;
    }

    setSelectionError(null);
    setIsSelecting(true);
    try {
      if (!auth.selectCompany) {
        throw new Error('Company selection is unavailable in this context');
      }

      await auth.selectCompany(selectedMembership.companyId);
      navigate('/', { replace: true });
    } catch (error) {
      setSelectionError(getApiErrorUserMessage(error));
    } finally {
      setIsSelecting(false);
    }
  };

  if (isAuthLoading) {
    return (
      <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading memberships...</Typography>
      </Stack>
    );
  }

  if (!bootstrap) {
    return (
      <Alert severity="warning">Session bootstrap is unavailable. Sign in again to continue.</Alert>
    );
  }

  if (memberships.length === 0) {
    if (location.pathname !== '/onboarding' && location.pathname !== '/select-company') {
      return <Navigate to="/onboarding" replace />;
    }

    return (
      <Card variant="outlined" sx={{ maxWidth: 720 }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h5">No companies found</Typography>
            <Typography color="text.secondary">
              {hasPlatformOption
                ? 'Create a company or continue in Platform Workspace.'
                : 'Create a company to continue.'}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button component={NavLink} to="/onboarding" variant="contained">
                Create company
              </Button>
              {hasPlatformOption && (
                <Button component={NavLink} to="/platform" variant="outlined">
                  Platform workspace
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ maxWidth: 720 }}>
      <CardContent>
        <Stack spacing={2.5}>
          <Stack spacing={0.5}>
            <Typography variant="h5">
              {hasPlatformOption ? 'Select workspace' : 'Select a company'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hasPlatformOption
                ? 'Choose the platform workspace or a company session for this login.'
                : 'Choose the company session for this login.'}
            </Typography>
          </Stack>

          {location.state &&
            (location.state as { workspaceUnavailable?: string }).workspaceUnavailable ===
              'platform' && (
              <Alert severity="info">Platform workspace is not available for this account.</Alert>
            )}

          <TextField
            select
            label={hasPlatformOption ? 'Company workspace' : 'Company'}
            value={selectedCompanyId ?? ''}
            disabled={isSelecting}
            onChange={(event) => {
              void handleCompanyChange(event);
            }}
            helperText={
              hasPlatformOption
                ? 'Company modules require a company-scoped session.'
                : 'A company-scoped session is required before loading modules.'
            }
            fullWidth
          >
            <MenuItem value="" disabled>
              Select company
            </MenuItem>
            {memberships.map((membership) => (
              <MenuItem key={membership.companyId} value={membership.companyId}>
                {membership.companyName}
              </MenuItem>
            ))}
          </TextField>

          {isSelecting && <Alert severity="info">Starting scoped session...</Alert>}
          {selectionError && <Alert severity="error">{selectionError}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {selectedMembership && (
              <Button
                variant="contained"
                disabled={isSelecting}
                onClick={() => {
                  void handleOpenSelectedCompany();
                }}
              >
                Open {selectedMembership.companyName}
              </Button>
            )}
            <Button component={NavLink} to="/onboarding" variant="outlined">
              Create company
            </Button>
            {hasPlatformOption && (
              <Button component={NavLink} to="/platform" variant="outlined">
                Platform workspace
              </Button>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
